import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useTheme } from '../../context/ThemeContext';
import {
  Flame,
  Phone,
  MessageSquare,
  Calendar,
  TrendingUp,
  Clock,
  AlertCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HotLeads() {
  const [hotLeads, setHotLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, grade_a_count: 0, grade_b_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { theme } = useTheme();

  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => {
    loadHotLeads();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadHotLeads, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadHotLeads = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/follow-ups/hot-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setHotLeads(data.hot_leads || []);
        setStats({
          total: data.total || 0,
          grade_a_count: data.grade_a_count || 0,
          grade_b_count: data.grade_b_count || 0
        });
      }
    } catch (err) {
      console.error('Error loading hot leads:', err);
    }
    setLoading(false);
    setRefreshing(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-600';
    if (score >= 60) return 'from-yellow-500 to-orange-600';
    if (score >= 40) return 'from-orange-500 to-red-600';
    return 'from-gray-500 to-gray-600';
  };

  const getGradeBadge = (grade) => {
    const colors = {
      'A': 'bg-green-500/20 text-green-400 border-green-500/30',
      'B': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'C': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'D': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[grade] || colors['D'];
  };

  const getSourceIcon = (source) => {
    if (source?.includes('facebook') || source?.includes('fb')) return '📘';
    if (source?.includes('instagram') || source?.includes('ig')) return '📸';
    if (source?.includes('whatsapp') || source?.includes('wa')) return '💬';
    if (source?.includes('dm')) return '✉️';
    return '👤';
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    return `hace ${diffDays}d`;
  };

  const openWhatsApp = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone.startsWith('52') ? cleanPhone : '52' + cleanPhone}`, '_blank');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 ${textPrimary}`}>
              <Flame className="w-7 h-7 text-orange-500" />
              Hot Leads
            </h1>
            <p className={textSecondary}>Leads con alta probabilidad de conversión (score ≥ 60)</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => loadHotLeads(true)}
            disabled={refreshing}
            className={theme === 'dark' ? 'border-slate-600' : 'border-gray-300'}
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={`${cardBg} border-l-4 border-l-orange-500`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textMuted}`}>Total Hot Leads</p>
                  <p className={`text-3xl font-bold text-orange-500`}>{stats.total}</p>
                </div>
                <Flame className="w-10 h-10 text-orange-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`${cardBg} border-l-4 border-l-green-500`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textMuted}`}>Grade A (80+)</p>
                  <p className={`text-3xl font-bold text-green-500`}>{stats.grade_a_count}</p>
                </div>
                <Star className="w-10 h-10 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className={`${cardBg} border-l-4 border-l-yellow-500`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textMuted}`}>Grade B (60-79)</p>
                  <p className={`text-3xl font-bold text-yellow-500`}>{stats.grade_b_count}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hot Leads List */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Leads que Requieren Atención Inmediata
            </CardTitle>
            <CardDescription className={textSecondary}>
              Ordenados por puntuación de mayor a menor
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : hotLeads.length > 0 ? (
              <div className="space-y-4">
                {hotLeads.map((lead) => (
                  <div 
                    key={lead.id} 
                    className={`p-4 rounded-xl border ${theme === 'dark' ? 'border-slate-600 bg-gradient-to-r from-slate-700/50 to-transparent' : 'border-gray-200 bg-gradient-to-r from-orange-50 to-transparent'} hover:shadow-lg transition-all`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Score Circle */}
                        <div className={`relative w-16 h-16 rounded-full bg-gradient-to-br ${getScoreColor(lead.lead_score)} flex items-center justify-center shadow-lg`}>
                          <div className="absolute inset-1 rounded-full bg-slate-900 flex items-center justify-center">
                            <div className="text-center">
                              <span className="text-xl font-bold text-white">{lead.lead_score}</span>
                              <span className="text-[10px] text-white/70 block -mt-1">pts</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Lead Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{getSourceIcon(lead.lead_source)}</span>
                            <h4 className={`font-semibold ${textPrimary}`}>{lead.lead_name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getGradeBadge(lead.score_grade)}`}>
                              Grade {lead.score_grade}
                            </span>
                            {lead.is_hot_lead && <span className="text-lg">🔥</span>}
                          </div>
                          
                          <div className={`flex items-center gap-4 text-sm ${textMuted}`}>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.lead_phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(lead.created_at)}
                            </span>
                          </div>
                          
                          {/* Score Factors */}
                          {lead.score_factors && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {Object.entries(lead.score_factors).map(([key, value]) => (
                                value > 0 && (
                                  <span 
                                    key={key} 
                                    className={`text-xs px-2 py-1 rounded-full ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'} ${textMuted}`}
                                  >
                                    {key === 'response_speed' && '⚡'}
                                    {key === 'interactions' && '💬'}
                                    {key === 'source' && '📍'}
                                    {key === 'keywords' && '🔑'}
                                    {key === 'recency' && '📅'}
                                    {' '}{key}: +{value}
                                  </span>
                                )
                              ))}
                            </div>
                          )}

                          {/* Interested Service */}
                          {lead.interested_service && (
                            <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400`}>
                              Interesado en: {lead.interested_service}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      <Button
                        onClick={() => openWhatsApp(lead.lead_phone)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        WhatsApp
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>

                    {/* Status Bar */}
                    <div className={`flex items-center justify-between mt-3 pt-3 border-t ${theme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                      <div className={`flex items-center gap-4 text-xs ${textMuted}`}>
                        <span>Day 0: {lead.day0_sent_at ? '✅' : '⏳'}</span>
                        <span>Day 3: {lead.day3_sent_at ? '✅' : '⏳'}</span>
                        <span>Day 7: {lead.day7_sent_at ? '✅' : '⏳'}</span>
                        {lead.last_response_at && (
                          <span className="text-green-400">
                            Última respuesta: {formatTimeAgo(lead.last_response_at)}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs ${lead.status === 'responded' ? 'text-green-400' : lead.status === 'new' ? 'text-blue-400' : textMuted}`}>
                        Estado: {lead.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${textMuted}`}>
                <Flame className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No hay hot leads en este momento</p>
                <p className="text-sm mt-1">Los leads con score ≥ 60 aparecerán aquí</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
